import {
    type BadgeConfig,
    COMBINED_PROGRAM_CONFIGS,
    EXCLUSIVE_LENS_CONFIGS,
    LENS_CREATOR_REWARDS_CONFIGS,
} from "./config";

import { ExclusiveLensStatus, LensCreatorPayoutEligibility } from "src/app/gql-generated/types";

export const isExclusiveActive = (status: ExclusiveLensStatus): boolean =>
    status === ExclusiveLensStatus.ExclusiveLensStatusExclusive ||
    status === ExclusiveLensStatus.ExclusiveLensStatusEligible;

export const isRewardsActive = (eligibility: LensCreatorPayoutEligibility): boolean =>
    eligibility === LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityEligible ||
    eligibility === LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityPending;

export const getBadgeConfig = (
    exclusiveLensStatus: ExclusiveLensStatus,
    lensCreatorPayoutEligibility: LensCreatorPayoutEligibility,
): BadgeConfig | null => {
    const exclusiveConfig = EXCLUSIVE_LENS_CONFIGS[exclusiveLensStatus];
    const rewardsConfig = LENS_CREATOR_REWARDS_CONFIGS[lensCreatorPayoutEligibility];

    if (exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusRemoved) {
        return exclusiveConfig;
    }

    const exclusiveActive = isExclusiveActive(exclusiveLensStatus);
    const rewardsActive = isRewardsActive(lensCreatorPayoutEligibility);

    // Both programs have some status (active or ineligible)
    if (exclusiveConfig && rewardsConfig) {
        // Both fully active (exclusive + eligible)
        if (
            exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusExclusive &&
            lensCreatorPayoutEligibility === LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityEligible
        ) {
            return COMBINED_PROGRAM_CONFIGS.bothActive;
        }

        // Both active but not fully (eligible/pending combinations)
        if (exclusiveActive && rewardsActive) {
            return COMBINED_PROGRAM_CONFIGS.bothEligible;
        }

        // Both ineligible
        if (
            exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusIneligible &&
            lensCreatorPayoutEligibility === LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityIneligible
        ) {
            return COMBINED_PROGRAM_CONFIGS.bothIneligible;
        }
    }

    // Single program active - prioritize exclusive lens if both have configs
    if (exclusiveConfig && !rewardsActive) {
        return exclusiveConfig;
    }

    if (rewardsConfig && !exclusiveActive) {
        return rewardsConfig;
    }

    // If only one has a config, show that one
    return exclusiveConfig ?? rewardsConfig;
};

export const isEligibleForAnyProgram = (
    exclusiveLensStatus: ExclusiveLensStatus,
    lensCreatorPayoutEligibility: LensCreatorPayoutEligibility,
): boolean => {
    return (
        EXCLUSIVE_LENS_CONFIGS[exclusiveLensStatus] !== null ||
        LENS_CREATOR_REWARDS_CONFIGS[lensCreatorPayoutEligibility] !== null
    );
};
